import {
  ApiRef,
  createApiRef,
  DiscoveryApi,
  FetchApi,
} from '@backstage/core-plugin-api';
import { BackendAuthApi } from '@internal/plugin-neo4j-auth-common';
import {
  AdminApi,
  Configuration,
  FetchError,
  GetInternalProjectScopedOverview200Response,
  GetInternalProjectScopedOverviewRequest,
  instanceOfErrorResponse,
  InternalApi,
  ListAdminOrganizations200Response,
  ListAdminRegions200Response,
  ListAllPESAvailableRegions200Response,
  ListAllPESAvailableRegionsRequest,
  ListAllPESEnabledRegionsRequest,
  MarkRegionAvailable200Response,
  MarkRegionAvailableRequest,
  PrivateEndpointServiceApi,
  RemoveGlobalPreReleaseRequest,
  RemoveRegionalPreReleaseRequest,
  ResponseError,
  SetGlobalPreRelease200Response,
  SetGlobalPreReleaseOperationRequest,
  SetRegionalPreRelease200Response,
  SetRegionalPreReleaseOperationRequest,
} from '@internal/backstage-plugin-neo4j-private-link-common';

import { PRIVATE_ENDPOINT_SERVICE_API_BASE_URL } from './const';
import { PrivateEndpointServiceApiError } from './errors';

const DEFAULT_ERROR_MESSAGE = 'Unknown error';
const DEFAULT_ERROR_STATUS = 400;

function errorMessageFrom(error: unknown): string {
  if (error instanceof FetchError) {
    return error.cause.message ?? error.message ?? DEFAULT_ERROR_MESSAGE;
  }

  return DEFAULT_ERROR_MESSAGE;
}

export class PrivateEndpointServiceRestApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApiBackstage: FetchApi;
  private readonly backendAuthApi: BackendAuthApi;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi: FetchApi;
    backendAuthApi: BackendAuthApi;
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApiBackstage = options.fetchApi;
    this.backendAuthApi = options.backendAuthApi;
  }

  private async createConfiguration(): Promise<Configuration> {
    const baseUrl = await this.discoveryApi.getBaseUrl('proxy');
    const token = await this.backendAuthApi.getToken();

    return new Configuration({
      basePath: `${baseUrl}${PRIVATE_ENDPOINT_SERVICE_API_BASE_URL}`,
      fetchApi: this.fetchApiBackstage.fetch,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  }

  private async getPrivateEndpointServiceApi(): Promise<PrivateEndpointServiceApi> {
    return new PrivateEndpointServiceApi(await this.createConfiguration());
  }

  private async getInternalApi(): Promise<InternalApi> {
    return new InternalApi(await this.createConfiguration());
  }

  private async getAdminApi(): Promise<AdminApi> {
    return new AdminApi(await this.createConfiguration());
  }

  async listAvailableRegions(
    requestParameters: ListAllPESAvailableRegionsRequest,
    options?: { signal?: AbortSignal },
  ): Promise<ListAllPESAvailableRegions200Response> {
    try {
      const api = await this.getPrivateEndpointServiceApi();
      return await api.listAllPESAvailableRegions(requestParameters, options);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async listEnabledRegions(
    requestParameters: ListAllPESEnabledRegionsRequest,
    options?: { signal?: AbortSignal },
  ): Promise<ListAllPESAvailableRegions200Response> {
    try {
      const api = await this.getPrivateEndpointServiceApi();
      return await api.listAllPESEnabledRegions(requestParameters, options);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async getOverview(
    requestParameters: GetInternalProjectScopedOverviewRequest,
    options?: { signal?: AbortSignal },
  ): Promise<GetInternalProjectScopedOverview200Response> {
    try {
      const api = await this.getInternalApi();
      return await api.getInternalProjectScopedOverview(
        requestParameters,
        options,
      );
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async listAdminRegions(options?: {
    signal?: AbortSignal;
  }): Promise<ListAdminRegions200Response> {
    try {
      const api = await this.getAdminApi();
      return await api.listAdminRegions(options);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async markRegionAvailable(
    requestParameters: MarkRegionAvailableRequest,
    options?: { signal?: AbortSignal },
  ): Promise<MarkRegionAvailable200Response> {
    try {
      const api = await this.getAdminApi();
      return await api.markRegionAvailable(requestParameters, options);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async listAdminOrganizations(options?: {
    signal?: AbortSignal;
  }): Promise<ListAdminOrganizations200Response> {
    try {
      const api = await this.getAdminApi();
      return await api.listAdminOrganizations(options);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async setGlobalPreRelease(
    requestParameters: SetGlobalPreReleaseOperationRequest,
    options?: { signal?: AbortSignal },
  ): Promise<SetGlobalPreRelease200Response> {
    try {
      const api = await this.getAdminApi();
      return await api.setGlobalPreRelease(requestParameters, options);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async removeGlobalPreRelease(
    requestParameters: RemoveGlobalPreReleaseRequest,
    options?: { signal?: AbortSignal },
  ): Promise<void> {
    try {
      const api = await this.getAdminApi();
      return await api.removeGlobalPreRelease(requestParameters, options);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async setRegionalPreRelease(
    requestParameters: SetRegionalPreReleaseOperationRequest,
    options?: { signal?: AbortSignal },
  ): Promise<SetRegionalPreRelease200Response> {
    try {
      const api = await this.getAdminApi();
      return await api.setRegionalPreRelease(requestParameters, options);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  async removeRegionalPreRelease(
    requestParameters: RemoveRegionalPreReleaseRequest,
    options?: { signal?: AbortSignal },
  ): Promise<void> {
    try {
      const api = await this.getAdminApi();
      return await api.removeRegionalPreRelease(requestParameters, options);
    } catch (error) {
      return this.handleSdkError(error);
    }
  }

  private async handleSdkError(error: unknown): Promise<never> {
    if (error instanceof ResponseError) {
      const json = await error.response.json();

      if (instanceOfErrorResponse(json)) {
        const fallback = json.errors[0]?.message ?? DEFAULT_ERROR_MESSAGE;

        throw new PrivateEndpointServiceApiError(
          fallback,
          error.response.status,
        );
      }
    }

    throw new PrivateEndpointServiceApiError(
      errorMessageFrom(error),
      DEFAULT_ERROR_STATUS,
    );
  }
}

export const privateEndpointServiceApiRef: ApiRef<PrivateEndpointServiceRestApi> =
  createApiRef<PrivateEndpointServiceRestApi>({
    id: 'plugin.neo4j-private-link.service',
  });
